/**
 * Portfolio Holdings Page (Web)
 * Detailed holdings list with filters
 * 
 * ⚠️ CRITICAL: Per ADR-FE-001, this component only displays pre-calculated data.
 * All portfolio calculations are performed by backend Portfolio Service.
 */

import React, { useEffect, useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Select,
  TableSortLabel,
  Menu,
  Card,
  Collapse,
  Divider,
  Chip,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Clear as ClearIcon,
  FileDownload as FileDownloadIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { useConsolidatedPortfolio } from '@shared/features/portfolio';
import { useBrokerAccounts } from '@shared/features/broker-accounts';
import { useAuth } from '@shared/features/auth';
import { AuthenticatedLayout } from '../../components/layout/AuthenticatedLayout';
import { formatCurrency, formatPercentage, formatNumber } from '@shared/utils/formatters';
import { exportToExcel, exportToPDF } from '@shared/utils/export';

export function PortfolioHoldingsPage() {
  const { accounts } = useBrokerAccounts();
  const { firebase_uid } = useAuth();
  const {
    holdings,
    loading,
    error,
    refreshHoldings,
  } = useConsolidatedPortfolio({ includeHoldings: true });

  const [selectedBrokerAccount, setSelectedBrokerAccount] = useState<string>('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [orderBy, setOrderBy] = useState<string>('');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set());

  useEffect(() => {
    refreshHoldings(selectedBrokerAccount || undefined);
  }, [selectedBrokerAccount]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshHoldings(selectedBrokerAccount || undefined);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      const columns = [
        { key: 'symbol', label: 'Symbol' },
        { key: 'brokerAccount', label: 'Broker Account' },
        { key: 'broker', label: 'Broker' },
        { key: 'quantity', label: 'Quantity' },
        { key: 'averagePrice', label: 'Avg.Cost' },
        { key: 'marketValue', label: 'Cur.Value' },
        { key: 'investedValue', label: 'Invested Value' },
        { key: 'pnl', label: 'P&L' },
        { key: 'dayChange', label: 'Day Change' },
        { key: 'netChange', label: 'Net Change' },
      ];

      const exportData = filteredHoldings.map((holding) => ({
        symbol: holding.symbol || 'N/A',
        brokerAccount: (() => {
          const firebaseUid = holding.firebase_uid?.trim();
          let brokerAccountId = holding.broker_account_id?.trim();
          
          // If broker_account_id is a UUID, look up the actual broker_account_id
          if (brokerAccountId && uuidToBrokerAccountId.has(brokerAccountId)) {
            brokerAccountId = uuidToBrokerAccountId.get(brokerAccountId)!;
          }
          
          // Use composite key: firebase_uid:broker_account_id
          if (firebaseUid && brokerAccountId) {
            const compositeKey = `${firebaseUid}:${brokerAccountId}`;
            return accountNameMap.get(compositeKey) || 'N/A';
          }
          
          return 'N/A';
        })(),
        broker: holding.broker_type || 'N/A',
        quantity: holding.quantity || 0,
        averagePrice: holding.average_price !== undefined ? holding.average_price : '—',
        marketValue: holding.market_value || 0,
        investedValue: holding.invested_value !== undefined ? holding.invested_value : '—',
        pnl: holding.unrealized_pnl !== undefined ? holding.unrealized_pnl : '—',
        dayChange: holding.day_change !== undefined ? holding.day_change : '—',
        netChange: holding.net_change !== undefined ? holding.net_change : '—',
      }));

      await exportToExcel(exportData, columns, `portfolio-holdings-${new Date().toISOString().split('T')[0]}`);
      setDownloadMenuAnchor(null);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    }
  };

  const handleExportPDF = async () => {
    try {
      const columns = [
        { key: 'symbol', label: 'Symbol' },
        { key: 'brokerAccount', label: 'Broker Account' },
        { key: 'broker', label: 'Broker' },
        { key: 'quantity', label: 'Quantity' },
        { key: 'averagePrice', label: 'Avg.Cost' },
        { key: 'marketValue', label: 'Cur.Value' },
        { key: 'investedValue', label: 'Invested Value' },
        { key: 'pnl', label: 'P&L' },
        { key: 'dayChange', label: 'Day Change' },
        { key: 'netChange', label: 'Net Change' },
      ];

      const exportData = filteredHoldings.map((holding) => ({
        symbol: holding.symbol || 'N/A',
        brokerAccount: (() => {
          const firebaseUid = holding.firebase_uid?.trim();
          let brokerAccountId = holding.broker_account_id?.trim();
          
          // If broker_account_id is a UUID, look up the actual broker_account_id
          if (brokerAccountId && uuidToBrokerAccountId.has(brokerAccountId)) {
            brokerAccountId = uuidToBrokerAccountId.get(brokerAccountId)!;
          }
          
          // Use composite key: firebase_uid:broker_account_id
          if (firebaseUid && brokerAccountId) {
            const compositeKey = `${firebaseUid}:${brokerAccountId}`;
            return accountNameMap.get(compositeKey) || 'N/A';
          }
          
          return 'N/A';
        })(),
        broker: holding.broker_type || 'N/A',
        quantity: holding.quantity || 0,
        averagePrice: holding.average_price !== undefined ? formatCurrency(holding.average_price) : '—',
        marketValue: formatCurrency(holding.market_value),
        investedValue: holding.invested_value !== undefined ? formatCurrency(holding.invested_value) : '—',
        pnl: holding.unrealized_pnl !== undefined ? formatCurrency(holding.unrealized_pnl, { showSign: true }) : '—',
        dayChange: holding.day_change !== undefined ? formatCurrency(holding.day_change, { showSign: true }) : '—',
        netChange: holding.net_change !== undefined ? formatCurrency(holding.net_change, { showSign: true }) : '—',
      }));

      await exportToPDF(exportData, columns, `portfolio-holdings-${new Date().toISOString().split('T')[0]}`, 'Portfolio Holdings');
      setDownloadMenuAnchor(null);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
    }
  };

  const handleDownloadMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  const toggleAccountExpansion = (accountId: string) => {
    setExpandedAccounts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(accountId)) {
        newSet.delete(accountId);
      } else {
        newSet.add(accountId);
      }
      return newSet;
    });
  };

  const filteredHoldings = selectedBrokerAccount
    ? holdings.filter((h) => h.broker_account_id === selectedBrokerAccount)
    : holdings;

  // Group holdings by broker_account_id
  const groupedHoldings = filteredHoldings.reduce((acc, holding) => {
    const accountId = holding.broker_account_id;
    if (!acc[accountId]) {
      acc[accountId] = [];
    }
    acc[accountId].push(holding);
    return acc;
  }, {} as Record<string, typeof filteredHoldings>);

  // Calculate totals for each broker account
  const accountTotals = Object.entries(groupedHoldings).reduce((acc, [accountId, accountHoldings]) => {
    acc[accountId] = {
      marketValue: accountHoldings.reduce((sum, h) => sum + (h.market_value || 0), 0),
      investedValue: accountHoldings.reduce((sum, h) => sum + (h.invested_value || 0), 0),
      pnl: accountHoldings.reduce((sum, h) => sum + (h.unrealized_pnl || 0), 0),
      dayChange: accountHoldings.reduce((sum, h) => sum + (h.day_change || 0), 0),
      count: accountHoldings.length,
    };
    return acc;
  }, {} as Record<string, { marketValue: number; investedValue: number; pnl: number; dayChange: number; count: number }>);

  // Create a map of firebase_uid:broker_account_id to account_name
  // Also create a UUID to broker_account_id mapping for holdings that have UUIDs
  const { accountNameMap, uuidToBrokerAccountId } = useMemo(() => {
    const map = new Map<string, string>();
    const uuidMap = new Map<string, string>(); // Map UUID (account.id) to broker_account_id
    
    accounts.forEach((account) => {
      const nameValue = account.account_name || account.broker_name || 'Unknown Account';
      
      // Map UUID to broker_account_id for lookup
      uuidMap.set(account.id, account.broker_account_id);
      
      // Create composite key: firebase_uid:broker_account_id
      if (firebase_uid) {
        const compositeKey = `${firebase_uid}:${account.broker_account_id}`;
        map.set(compositeKey, nameValue);
      }
    });
    
    // Also handle holdings that might have different firebase_uid
    if (holdings.length > 0) {
      const uniqueFirebaseUids = [...new Set(holdings.map(h => h.firebase_uid).filter(Boolean))];
      uniqueFirebaseUids.forEach(uid => {
        accounts.forEach((account) => {
          const nameValue = account.account_name || account.broker_name || 'Unknown Account';
          const compositeKey = `${uid}:${account.broker_account_id}`;
          map.set(compositeKey, nameValue);
        });
      });
    }
    
    
    return { accountNameMap: map, uuidToBrokerAccountId: uuidMap };
  }, [accounts, holdings, firebase_uid]);

  // Sorting function
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <AuthenticatedLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Filters */}
        {accounts.length > 0 && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" justifyContent="space-between">
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Broker Account</InputLabel>
                  <Select
                    value={selectedBrokerAccount}
                    label="Broker Account"
                    onChange={(e: SelectChangeEvent<string>) => setSelectedBrokerAccount(e.target.value)}
                    sx={{
                      height: '32px',
                      fontSize: '0.75rem',
                      '& .MuiSelect-select': {
                        fontSize: '0.75rem',
                        py: '4px',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          '& .MuiMenuItem-root': {
                            fontSize: '0.75rem',
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.75rem' }}>All Accounts</MenuItem>
                    {accounts.map((account) => (
                      <MenuItem key={account.id} value={account.id} sx={{ fontSize: '0.75rem' }}>
                        {account.account_name || account.broker_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                {filteredHoldings.length > 0 && (
                  <>
                    <Tooltip title="Download">
                      <IconButton
                        onClick={handleDownloadMenuOpen}
                        disabled={loading}
                        color="primary"
                        size="small"
                        sx={{ 
                          border: '1px solid', 
                          borderColor: 'primary.main',
                          borderRadius: '50%',
                          width: 32,
                          height: 32,
                        }}
                      >
                        <FileDownloadIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={downloadMenuAnchor}
                      open={Boolean(downloadMenuAnchor)}
                      onClose={handleDownloadMenuClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                    >
                      <MenuItem 
                        onClick={handleExportExcel}
                        sx={{ fontSize: '0.75rem' }}
                      >
                        EXCEL
                      </MenuItem>
                      <MenuItem 
                        onClick={handleExportPDF}
                        sx={{ fontSize: '0.75rem' }}
                      >
                        PDF
                      </MenuItem>
                    </Menu>
                  </>
                )}
                <Tooltip title="Refresh">
                  <IconButton
                    onClick={handleRefresh}
                    disabled={isRefreshing || loading}
                    color="primary"
                    size="small"
                    sx={{ 
                      border: '1px solid', 
                      borderColor: 'primary.main',
                      borderRadius: '50%',
                      width: 32,
                      height: 32,
                    }}
                  >
                    <RefreshIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
                {selectedBrokerAccount && (
                  <Button
                    size="small"
                    onClick={() => setSelectedBrokerAccount('')}
                    startIcon={<ClearIcon sx={{ fontSize: '0.875rem' }} />}
                    sx={{ 
                      whiteSpace: 'nowrap',
                      fontSize: '0.75rem',
                      height: '32px',
                      px: 1.5,
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Stack>
          </Paper>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && filteredHoldings.length > 0 && (
          <Stack spacing={2}>
            {Object.entries(groupedHoldings).map(([accountId, accountHoldings]) => {
              const firstHolding = accountHoldings[0];
              const firebaseUid = firstHolding?.firebase_uid?.trim();
              let brokerAccountId = accountId;
              
              // If accountId is a UUID, look up the actual broker_account_id
              if (accountId && uuidToBrokerAccountId.has(accountId)) {
                brokerAccountId = uuidToBrokerAccountId.get(accountId)!;
              }
              
              // Use composite key: firebase_uid:broker_account_id
              const compositeKey = firebaseUid && brokerAccountId ? `${firebaseUid}:${brokerAccountId}` : null;
              const accountName = compositeKey ? accountNameMap.get(compositeKey) || 'N/A' : 'N/A';
              // brokerAccountId is the actual broker account identifier (not UUID)
              const isExpanded = expandedAccounts.has(accountId);
              const totals = accountTotals[accountId];
              const accountIsProfit = (totals.pnl || 0) >= 0;
              const accountIsDayProfit = (totals.dayChange || 0) >= 0;
              const brokerType = accountHoldings[0]?.broker_type || 'N/A';

              // Sort holdings within this account
              const sortedAccountHoldings = [...accountHoldings].sort((a, b) => {
                if (!orderBy) return 0;
                
                let aValue: any;
                let bValue: any;

                switch (orderBy) {
                  case 'symbol':
                    aValue = (a.symbol || '').toLowerCase();
                    bValue = (b.symbol || '').toLowerCase();
                    break;
                  case 'quantity':
                    aValue = a.quantity || 0;
                    bValue = b.quantity || 0;
                    break;
                  case 'averagePrice':
                    aValue = a.average_price || 0;
                    bValue = b.average_price || 0;
                    break;
                  case 'netChange':
                    aValue = a.net_change || 0;
                    bValue = b.net_change || 0;
                    break;
                  case 'marketValue':
                    aValue = a.market_value || 0;
                    bValue = b.market_value || 0;
                    break;
                  case 'investedValue':
                    aValue = a.invested_value || 0;
                    bValue = b.invested_value || 0;
                    break;
                  case 'pnl':
                    aValue = a.unrealized_pnl || 0;
                    bValue = b.unrealized_pnl || 0;
                    break;
                  case 'dayChange':
                    aValue = a.day_change || 0;
                    bValue = b.day_change || 0;
                    break;
                  default:
                    return 0;
                }

                if (aValue < bValue) {
                  return order === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                  return order === 'asc' ? 1 : -1;
                }
                return 0;
              });

              return (
                <Card key={accountId} elevation={2} sx={{ overflow: 'visible' }}>
                  {/* Broker Account Header */}
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'rgba(4, 55, 123, 0.05)',
                      borderLeft: '4px solid',
                      borderColor: 'primary.main',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'rgba(4, 55, 123, 0.08)',
                      },
                    }}
                    onClick={() => toggleAccountExpansion(accountId)}
                  >
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={2} alignItems="center" flex={1}>
                        <IconButton
                          size="small"
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            toggleAccountExpansion(accountId);
                          }}
                          sx={{
                            bgcolor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                              bgcolor: 'primary.dark',
                            },
                            width: 32,
                            height: 32,
                          }}
                        >
                          {isExpanded ? (
                            <RemoveIcon sx={{ fontSize: '1.25rem' }} />
                          ) : (
                            <AddIcon sx={{ fontSize: '1.25rem' }} />
                          )}
                        </IconButton>
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="baseline">
                            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: '#04377b' }}>
                              {accountName}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              ({brokerAccountId})
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                              label={brokerType}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.6875rem',
                                textTransform: 'capitalize',
                                bgcolor: 'rgba(4, 55, 123, 0.1)',
                                color: '#04377b',
                              }}
                            />
                            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              {totals.count} {totals.count === 1 ? 'holding' : 'holdings'}
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={4} alignItems="center">
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" sx={{ fontSize: '0.6875rem', color: 'text.secondary', display: 'block' }}>
                            Market Value
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#04377b' }}>
                            {formatCurrency(totals.marketValue)}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" sx={{ fontSize: '0.6875rem', color: 'text.secondary', display: 'block' }}>
                            Invested Value
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#04377b' }}>
                            {formatCurrency(totals.investedValue)}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" sx={{ fontSize: '0.6875rem', color: 'text.secondary', display: 'block' }}>
                            P&L
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                            {accountIsProfit ? (
                              <TrendingUpIcon color="success" sx={{ fontSize: '1rem' }} />
                            ) : (
                              <TrendingDownIcon color="error" sx={{ fontSize: '1rem' }} />
                            )}
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: accountIsProfit ? 'success.main' : 'error.main',
                              }}
                            >
                              {formatCurrency(totals.pnl, { showSign: true })}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" sx={{ fontSize: '0.6875rem', color: 'text.secondary', display: 'block' }}>
                            Day Change
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                            {accountIsDayProfit ? (
                              <TrendingUpIcon color="success" sx={{ fontSize: '1rem' }} />
                            ) : (
                              <TrendingDownIcon color="error" sx={{ fontSize: '1rem' }} />
                            )}
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: accountIsDayProfit ? 'success.main' : 'error.main',
                              }}
                            >
                              {formatCurrency(totals.dayChange, { showSign: true })}
                            </Typography>
                          </Box>
                        </Box>
                      </Stack>
                    </Stack>
                  </Box>

                  {/* Holdings Table (Collapsible) */}
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Divider />
                    <TableContainer>
                      <Table size="small" sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid rgba(0, 0, 0, 0.08)' } }}>
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'rgba(4, 55, 123, 0.03)' }}>
                            <TableCell sx={{ fontWeight: 700, color: '#04377b', fontSize: '0.8125rem', py: 1.5, px: 3 }}>
                              <TableSortLabel
                                active={orderBy === 'symbol'}
                                direction={orderBy === 'symbol' ? order : 'asc'}
                                onClick={() => handleRequestSort('symbol')}
                                sx={{ 
                                  '& .MuiTableSortLabel-icon': { 
                                    fontSize: '0.875rem',
                                    color: '#04377b !important',
                                  },
                                  fontSize: '0.8125rem',
                                  fontWeight: 700,
                                  color: '#04377b',
                                }}
                              >
                                Symbol
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#04377b', fontSize: '0.8125rem', py: 1.5 }}>
                              <TableSortLabel
                                active={orderBy === 'quantity'}
                                direction={orderBy === 'quantity' ? order : 'asc'}
                                onClick={() => handleRequestSort('quantity')}
                                sx={{ 
                                  '& .MuiTableSortLabel-icon': { 
                                    fontSize: '0.875rem',
                                    color: '#04377b !important',
                                  },
                                  fontSize: '0.8125rem',
                                  fontWeight: 700,
                                  color: '#04377b',
                                }}
                              >
                                Quantity
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#04377b', fontSize: '0.8125rem', py: 1.5 }}>
                              <TableSortLabel
                                active={orderBy === 'averagePrice'}
                                direction={orderBy === 'averagePrice' ? order : 'asc'}
                                onClick={() => handleRequestSort('averagePrice')}
                                sx={{ 
                                  '& .MuiTableSortLabel-icon': { 
                                    fontSize: '0.875rem',
                                    color: '#04377b !important',
                                  },
                                  fontSize: '0.8125rem',
                                  fontWeight: 700,
                                  color: '#04377b',
                                }}
                              >
                                Avg. Cost
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#04377b', fontSize: '0.8125rem', py: 1.5 }}>
                              <TableSortLabel
                                active={orderBy === 'marketValue'}
                                direction={orderBy === 'marketValue' ? order : 'asc'}
                                onClick={() => handleRequestSort('marketValue')}
                                sx={{ 
                                  '& .MuiTableSortLabel-icon': { 
                                    fontSize: '0.875rem',
                                    color: '#04377b !important',
                                  },
                                  fontSize: '0.8125rem',
                                  fontWeight: 700,
                                  color: '#04377b',
                                }}
                              >
                                Current Value
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#04377b', fontSize: '0.8125rem', py: 1.5 }}>
                              <TableSortLabel
                                active={orderBy === 'investedValue'}
                                direction={orderBy === 'investedValue' ? order : 'asc'}
                                onClick={() => handleRequestSort('investedValue')}
                                sx={{ 
                                  '& .MuiTableSortLabel-icon': { 
                                    fontSize: '0.875rem',
                                    color: '#04377b !important',
                                  },
                                  fontSize: '0.8125rem',
                                  fontWeight: 700,
                                  color: '#04377b',
                                }}
                              >
                                Invested Value
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#04377b', fontSize: '0.8125rem', py: 1.5 }}>
                              <TableSortLabel
                                active={orderBy === 'pnl'}
                                direction={orderBy === 'pnl' ? order : 'asc'}
                                onClick={() => handleRequestSort('pnl')}
                                sx={{ 
                                  '& .MuiTableSortLabel-icon': { 
                                    fontSize: '0.875rem',
                                    color: '#04377b !important',
                                  },
                                  fontSize: '0.8125rem',
                                  fontWeight: 700,
                                  color: '#04377b',
                                }}
                              >
                                P&L
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#04377b', fontSize: '0.8125rem', py: 1.5 }}>
                              <TableSortLabel
                                active={orderBy === 'dayChange'}
                                direction={orderBy === 'dayChange' ? order : 'asc'}
                                onClick={() => handleRequestSort('dayChange')}
                                sx={{ 
                                  '& .MuiTableSortLabel-icon': { 
                                    fontSize: '0.875rem',
                                    color: '#04377b !important',
                                  },
                                  fontSize: '0.8125rem',
                                  fontWeight: 700,
                                  color: '#04377b',
                                }}
                              >
                                Day Change
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#04377b', fontSize: '0.8125rem', py: 1.5 }}>
                              <TableSortLabel
                                active={orderBy === 'netChange'}
                                direction={orderBy === 'netChange' ? order : 'asc'}
                                onClick={() => handleRequestSort('netChange')}
                                sx={{ 
                                  '& .MuiTableSortLabel-icon': { 
                                    fontSize: '0.875rem',
                                    color: '#04377b !important',
                                  },
                                  fontSize: '0.8125rem',
                                  fontWeight: 700,
                                  color: '#04377b',
                                }}
                              >
                                Net Change
                              </TableSortLabel>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedAccountHoldings.map((holding) => {
                            const isProfit = (holding.unrealized_pnl || 0) >= 0;
                            const isDayProfit = (holding.day_change || 0) >= 0;

                            return (
                              <TableRow
                                key={holding.id}
                                sx={{
                                  '&:nth-of-type(even)': { bgcolor: 'rgba(4, 55, 123, 0.02)' },
                                  '&:hover': { bgcolor: 'rgba(4, 55, 123, 0.05)' },
                                }}
                              >
                                <TableCell sx={{ py: 1.5, px: 3 }}>
                                  <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                                    {holding.symbol || 'N/A'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ py: 1.5 }}>
                                  <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                                    {formatNumber(holding.quantity, 0)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ py: 1.5 }}>
                                  <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                                    {holding.average_price !== undefined
                                      ? formatCurrency(holding.average_price)
                                      : '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ py: 1.5 }}>
                                  <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                                    {formatCurrency(holding.market_value)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ py: 1.5 }}>
                                  <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                                    {holding.invested_value !== undefined
                                      ? formatCurrency(holding.invested_value)
                                      : '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ py: 1.5 }}>
                                  {holding.unrealized_pnl !== undefined && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {isProfit ? (
                                          <TrendingUpIcon color="success" sx={{ fontSize: '1rem' }} />
                                        ) : (
                                          <TrendingDownIcon color="error" sx={{ fontSize: '1rem' }} />
                                        )}
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontSize: '0.8125rem',
                                            fontWeight: 500,
                                            color: isProfit ? 'success.main' : 'error.main',
                                          }}
                                        >
                                          {formatCurrency(holding.unrealized_pnl, { showSign: true })}
                                        </Typography>
                                      </Box>
                                      {holding.unrealized_pnl_percentage !== undefined && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontSize: '0.6875rem',
                                            color: isProfit ? 'success.main' : 'error.main',
                                          }}
                                        >
                                          {formatPercentage(holding.unrealized_pnl_percentage)}
                                        </Typography>
                                      )}
                                    </Box>
                                  )}
                                </TableCell>
                                <TableCell align="right" sx={{ py: 1.5 }}>
                                  {holding.day_change !== undefined && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {isDayProfit ? (
                                          <TrendingUpIcon color="success" sx={{ fontSize: '1rem' }} />
                                        ) : (
                                          <TrendingDownIcon color="error" sx={{ fontSize: '1rem' }} />
                                        )}
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontSize: '0.8125rem',
                                            fontWeight: 500,
                                            color: isDayProfit ? 'success.main' : 'error.main',
                                          }}
                                        >
                                          {formatCurrency(holding.day_change, { showSign: true })}
                                        </Typography>
                                      </Box>
                                      {holding.day_change_percentage !== undefined && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontSize: '0.6875rem',
                                            color: isDayProfit ? 'success.main' : 'error.main',
                                          }}
                                        >
                                          {formatPercentage(holding.day_change_percentage)}
                                        </Typography>
                                      )}
                                    </Box>
                                  )}
                                </TableCell>
                                <TableCell align="right" sx={{ py: 1.5 }}>
                                  {holding.net_change !== undefined ? (
                                    (() => {
                                      const isNetPositive = holding.net_change >= 0;
                                      return (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {isNetPositive ? (
                                              <TrendingUpIcon color="success" sx={{ fontSize: '1rem' }} />
                                            ) : (
                                              <TrendingDownIcon color="error" sx={{ fontSize: '1rem' }} />
                                            )}
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontSize: '0.8125rem',
                                                fontWeight: 500,
                                                color: isNetPositive ? 'success.main' : 'error.main',
                                              }}
                                            >
                                              {formatCurrency(holding.net_change, { showSign: true })}
                                            </Typography>
                                          </Box>
                                          {holding.net_change_percentage !== undefined && (
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                fontSize: '0.6875rem',
                                                color: isNetPositive ? 'success.main' : 'error.main',
                                              }}
                                            >
                                              {formatPercentage(holding.net_change_percentage)}
                                            </Typography>
                                          )}
                                        </Box>
                                      );
                                    })()
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                                      —
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Collapse>
                </Card>
              );
            })}
          </Stack>
        )}

        {!loading && filteredHoldings.length === 0 && !error && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              No Holdings Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedBrokerAccount
                ? 'No holdings found for the selected broker account.'
                : 'No holdings found. Connect a broker account to start tracking your portfolio.'}
            </Typography>
          </Paper>
        )}
      </Container>
    </AuthenticatedLayout>
  );
}
